import React, { useState } from "react";
import "./UseCases.css";

const useCases = [
  {
    tag: "Sales",
    title: "Sales Professionals",
    headline: "Share Contact Details Instantly at Events",
    description:
      "Skip searching for paper cards during meetings and instantly share your contact details, brochure, and website with one quick tap.",
    icon: (
      <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
        <rect width="52" height="52" rx="14" fill="#FFF0E8" />
        <path d="M16 36l7-10 5 6 4-5 7 9H16z" fill="#F36D24" opacity="0.2" />
        <path d="M14 20h6M14 25h10M14 30h8" stroke="#F36D24" strokeWidth="2.2" strokeLinecap="round" />
        <rect x="27" y="16" width="12" height="20" rx="2" stroke="#F36D24" strokeWidth="2.2" />
        <path d="M30 21h6M30 25h4" stroke="#F36D24" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    tag: "Freelance",
    title: "Freelancers & Consultants",
    headline: "Your Digital NFC Card as a Portfolio",
    description:
      "Share your portfolio, Instagram, LinkedIn, and past work instantly while meeting clients, without sending multiple links separately.",
    icon: (
      <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
        <rect width="52" height="52" rx="14" fill="#FFF0E8" />
        <circle cx="26" cy="22" r="6" stroke="#F36D24" strokeWidth="2.2" />
        <path d="M14 38c0-6.627 5.373-12 12-12s12 5.373 12 12" stroke="#F36D24" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M32 18l2-2M34 22h2M32 26l2 2" stroke="#F36D24" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    tag: "Retail",
    title: "Retail & Local Businesses",
    headline: "NFC Digital Card for Walk-In Customers",
    description:
      "Help customers save your contact details, location, offers, and social pages instantly while they visit your store or office.",
    icon: (
      <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
        <rect width="52" height="52" rx="14" fill="#FFF0E8" />
        <path d="M14 22l4-6h16l4 6v2a4 4 0 01-8 0 4 4 0 01-8 0 4 4 0 01-8 0v-2z" stroke="#F36D24" strokeWidth="2.2" strokeLinejoin="round" />
        <path d="M18 30v8h16v-8" stroke="#F36D24" strokeWidth="2.2" strokeLinejoin="round" />
        <rect x="22" y="32" width="8" height="6" rx="1" stroke="#F36D24" strokeWidth="2" />
      </svg>
    ),
  },
  {
    tag: "Corporate",
    title: "Corporate Teams",
    headline: "Branded Metal NFC Card for Every Employee",
    description:
      "Give your employees one professionally branded NFC card that keeps contact sharing consistent across meetings, expos, and client visits.",
    icon: (
      <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
        <rect width="52" height="52" rx="14" fill="#FFF0E8" />
        <rect x="14" y="20" width="24" height="16" rx="3" stroke="#F36D24" strokeWidth="2.2" />
        <path d="M20 20v-3a2 2 0 012-2h8a2 2 0 012 2v3" stroke="#F36D24" strokeWidth="2.2" />
        <path d="M14 27h24" stroke="#F36D24" strokeWidth="2" />
        <circle cx="26" cy="27" r="2" fill="#F36D24" />
      </svg>
    ),
  },
  {
    tag: "Real Estate",
    title: "Real Estate Agents",
    headline: "Instantly Share Property Details with Clients",
    description:
      "Share property listings, brochures, contact details, and location links instantly during site visits without carrying printed documents everywhere.",
    icon: (
      <svg width="52" height="52" viewBox="0 0 52 52" fill="none">
        <rect width="52" height="52" rx="14" fill="#FFF0E8" />
        <path d="M14 36V22l12-8 12 8v14H14z" stroke="#F36D24" strokeWidth="2.2" strokeLinejoin="round" />
        <rect x="22" y="28" width="8" height="8" rx="1" stroke="#F36D24" strokeWidth="2" />
        <path d="M26 14v2" stroke="#F36D24" strokeWidth="2" strokeLinecap="round" />
      </svg>
    ),
  },
];

export default function UseCases() {
  const [active, setActive] = useState(0);
  const current = useCases[active];

  return (
    <section id="use-cases" className="uc-section">
      <div className="uc-container">
        <div className="uc-header">
          <h2 className="uc-title">
            Use Cases of Our NFC Cards{" "}
            <span className="uc-title-accent">Across Industries</span>
          </h2>
          <p className="uc-subtitle">
            Our NFC card for business helps professionals share details faster
            and look more professional in every interaction.
          </p>
        </div>

        <div className="uc-body">
          {/* Tab list */}
          <div className="uc-tabs">
            {useCases.map((item, i) => (
              <button
                key={i}
                className={`uc-tab${active === i ? " uc-tab--active" : ""}`}
                onClick={() => setActive(i)}
              >
                <span className="uc-tab-number">0{i + 1}</span>
                <span className="uc-tab-label">
                  <span className="uc-tab-tag">{item.tag}</span>
                  <span className="uc-tab-title">{item.title}</span>
                </span>
                <span className="uc-tab-arrow">→</span>
              </button>
            ))}
          </div>

          {/* Content panel */}
          <div className="uc-panel" key={active}>
            <div className="uc-panel-icon">{current.icon}</div>
            <p className="uc-panel-tag">{current.tag}</p>
            <h3 className="uc-panel-title">{current.title}</h3>
            <h4 className="uc-panel-headline">{current.headline}</h4>
            <p className="uc-panel-desc">{current.description}</p>
            <div className="uc-panel-bar" />
          </div>
        </div>

        {/* Mobile cards */}
        <div className="uc-mobile-cards">
          {useCases.map((item, i) => (
            <div key={i} className="uc-mobile-card">
              <div className="uc-mobile-card-top">
                {item.icon}
                <div>
                  <span className="uc-mobile-tag">{item.tag}</span>
                  <h3 className="uc-mobile-title">{item.title}</h3>
                  <h4 className="uc-mobile-headline">{item.headline}</h4>
                </div>
              </div>
              <p className="uc-mobile-desc">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
